import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  AccessLogView, BrandingView, CustomFieldDefinitionView, CustomFieldTarget, GrantView,
  OverrideView,
} from '../types'

const BASE = '/v1/admin'

// --- custom fields ---------------------------------------------------------

export function useCustomFields(targetType: CustomFieldTarget) {
  return useQuery({
    queryKey: ['admin', 'custom-fields', targetType],
    queryFn: async () =>
      (await apiClient.get<CustomFieldDefinitionView[]>(`${BASE}/custom-fields`, {
        params: { targetType },
      })).data,
  })
}

/**
 * The fields a form should render, in display order.
 *
 * Deliberately the same endpoint the admin screen previews from, so what an administrator
 * sees while designing a form is what the form will actually be — not a second rendering
 * path that can drift.
 */
export function useCustomFieldSchema(targetType: CustomFieldTarget, targetId?: string) {
  return useQuery({
    queryKey: ['admin', 'custom-fields', 'schema', targetType, targetId],
    queryFn: async () =>
      (await apiClient.get<CustomFieldDefinitionView[]>(`${BASE}/custom-fields/schema`, {
        params: { targetType, targetId },
      })).data,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCustomFieldMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'custom-fields'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<CustomFieldDefinitionView>(`${BASE}/custom-fields`, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<CustomFieldDefinitionView>(`${BASE}/custom-fields/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    withdraw: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`${BASE}/custom-fields/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

// --- branding --------------------------------------------------------------

export function useBranding(legalEntityId?: string) {
  return useQuery({
    queryKey: ['admin', 'branding', legalEntityId],
    queryFn: async () =>
      (await apiClient.get<BrandingView>(`${BASE}/security/branding`, {
        params: { legalEntityId },
      })).data,
  })
}

export function useBrandingMutations() {
  const queryClient = useQueryClient()
  return {
    save: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.put<BrandingView>(`${BASE}/security/branding`, body)).data,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'branding'] }),
    }),
  }
}

// --- entity grants ---------------------------------------------------------

export function useGrants(userId?: string) {
  return useQuery({
    queryKey: ['admin', 'grants', userId],
    queryFn: async () =>
      (await apiClient.get<GrantView[]>(`${BASE}/security/grants`, { params: { userId } })).data,
  })
}

/** What a proposed grant would let somebody reach, before it is made. */
export function useGrantPreview(userId?: string, roleId?: string, legalEntityId?: string) {
  return useQuery({
    queryKey: ['admin', 'grants', 'preview', userId, roleId, legalEntityId],
    enabled: Boolean(userId && roleId),
    queryFn: async () =>
      (await apiClient.get<unknown>(`${BASE}/security/grants/preview`, {
        params: { userId, roleId, legalEntityId },
      })).data,
  })
}

export function useGrantMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'grants'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<GrantView>(`${BASE}/security/grants`, body)).data,
      onSuccess: invalidate,
    }),
    revoke: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`${BASE}/security/grants/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

// --- segregation of duties -------------------------------------------------

/** The permission pairs nobody may hold together, as the server defines them. */
export function useSodConflicts() {
  return useQuery({
    queryKey: ['admin', 'sod', 'conflicts'],
    queryFn: async () =>
      (await apiClient.get<string[]>('/v1/admin/security/conflicts')).data,
    // Rules change when the code changes, not between renders.
    staleTime: 5 * 60_000,
  })
}

export function useSodOverrides() {
  return useQuery({
    queryKey: ['admin', 'sod', 'overrides'],
    queryFn: async () =>
      (await apiClient.get<OverrideView[]>('/v1/admin/security/overrides')).data,
  })
}

export function useSodOverrideMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'sod'] })

  return {
    // Recording an override needs admin:system, not admin:grants — deliberately a different
    // and higher permission than the one that hands out the grants it excuses.
    record: useMutation({
      mutationFn: async (body: {
        userId: string
        legalEntityId: string | null
        firstPermission: string
        secondPermission: string
        reason: string
        expiresAt: string | null
      }) => (await apiClient.post<OverrideView>('/v1/admin/security/overrides', body)).data,
      onSuccess: invalidate,
    }),
    revoke: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`/v1/admin/security/overrides/${id}`)
      },
      onSuccess: invalidate,
    }),
  }
}

// --- sensitive access log --------------------------------------------------

export interface AccessLogQuery {
  entityType?: string
  userId?: string
  page: number
  size: number
}

export function useAccessLog(query: AccessLogQuery) {
  return useQuery({
    queryKey: ['admin', 'access-log', query],
    queryFn: async () =>
      (await apiClient.get<PageResponse<AccessLogView>>('/v1/admin/security/access-log', {
        params: {
          entityType: query.entityType || undefined,
          userId: query.userId || undefined,
          page: query.page,
          size: query.size,
        },
      })).data,
    // Paging through an audit trail should not blank the table between pages.
    placeholderData: (previous) => previous,
  })
}

/**
 * Pickers for the entity-grant dialog, which used to take a pasted user id and role id.
 * Both endpoints return plain arrays. The user list needs ADMIN_USER_MANAGE, which a
 * grants-only administrator may lack — the dialog falls back to an id field when it fails.
 */
export function useRolePicker(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'security', 'roles', 'picker'],
    enabled,
    queryFn: async () =>
      (await apiClient.get<{ id: string; code: string; name: string }[]>(`${BASE}/security/roles`)).data,
    staleTime: 5 * 60_000,
  })
}

export function useUserPicker(enabled = true) {
  return useQuery({
    queryKey: ['administration', 'users', 'picker'],
    enabled,
    queryFn: async () =>
      (await apiClient.get<{ id: string; email: string; fullName: string }[]>('/v1/administration/users')).data,
    staleTime: 60_000,
    retry: false,
  })
}
